import _ from 'lodash';
import initialstate from '../initialstate';
import * as types from '../../actions/actiontypes';
import {PaperProfileIDs, TaskStatusIds} from '../../util/uiconstants';
import {isApiResponseOk, normalize, normalizedUpdate} from '../../util/utils';

export default function markCompleteTasksReducer(state = initialstate.markCompleteTasks, action) {
	let newState;

	switch (action.type) {
		case types.RESET_BULK_MARK_COMPLETE: {
			let initialState = {byId: {}, allIds: []};
			return { tasks: initialState, documents: initialState};
		}
		case types.MARK_COMPLETE_GET_LINKED_FORMS: {
			if (isApiResponseOk(action)) {
				const payloadDocuments = [];
				const payloadTasks = {}; // Using a dictionary to avoid duplicates and optimize searches

				// Loop throug action.response, and for each item, accumulate each task in the relatedTasks property into a tasks array. Tasks may be repeated among documents, so use the id prop to avoid duplicates
				for (const doc of action.response.filter((d) => d.engagementId === action.engagementId)) {
					for (let task of doc.relatedTasks) {
						// Remove after API response fix
						task = {
							...task,
							id: task.taskId
						};
						if (!payloadTasks[task.id]) {
							payloadTasks[task.id] = {...task};
						}

						// Add the current document to the task's documents array
						if (!payloadTasks[task.id].documents) {
							payloadTasks[task.id].documents = [];
						}

						if (!payloadTasks[task.id].documents.includes(doc.id)) {
							payloadTasks[task.id].documents.push(doc.id);
						}
					}

					doc.tasks = doc.relatedTasks.map((task) => task.taskId);
					payloadDocuments.push(doc);
				}

				const sortedTasks = _.sortBy(Object.values(payloadTasks), ['taskDisplayName']);

				newState = {
					...state,
					documents: normalize(payloadDocuments, undefined, state.documents),
					tasks: normalize(sortedTasks, 'taskId', state.tasks)
				};
			}
			break;
		}
		case types.GET_TASK_DOCUMENTS: {
			if (isApiResponseOk(action) && action.response?.data?.length > 0) {
				newState = {...state};
				let taskDocumentIdList = [];

				// Excluding paper profile documents from the response
				for (const responseDoc of action.response.data.filter((doc) => !PaperProfileIDs.includes(doc.documentTypeId))) {
					const payloadDoc = {
						...responseDoc,
						approvals: responseDoc.approvals.map((app) => app.id),
						tasks: [...responseDoc.tasks.map((task) => task.id), action.taskId]
					};

					newState.documents = normalizedUpdate(responseDoc.id, payloadDoc, newState.documents);
					taskDocumentIdList.push(responseDoc.id);
				}

				newState.tasks = normalizedUpdate(action.taskId, {documents: taskDocumentIdList}, newState.tasks);
			}
			break;
		}
		case types.MARK_COMPLETE_GET_DOCUMENTS: {
			if (isApiResponseOk(action) && action.response?.data?.length > 0) {
				// Update approvals (signoffs) and related tasks details only
				// Also update documents property for each task included among the documents
				let docsPayload = [];
				let tasksPayload = [];
				for (const responseDocument of action.response.data.filter(
					(doc) => !PaperProfileIDs.includes(doc.documentTypeId)
				)) {
					// TODO Make this simpler...
					for (const docTask of responseDocument.tasks) {
						// Get the task from the state, and add the current document's id to the documents property if not present
						let stateTask;
						let payloadTask;

						if (tasksPayload.length > 0) {
							payloadTask = tasksPayload.find((t) => t.id === docTask.id);
						}
						if (!payloadTask) {
							stateTask = state.tasks.byId[docTask.id];
							if (stateTask) {
								payloadTask = {
									id: stateTask.id,
									documents: [...stateTask.documents]
								};
							}
						}

						if (payloadTask) {
							if (!payloadTask.documents.includes(responseDocument.id)) {
								payloadTask.documents.push(responseDocument.id);
							}

							if (!tasksPayload.includes((task) => task.id === payloadTask.id)) {
								tasksPayload.push(payloadTask);
							}
						}
					}

					const payloadDoc = {
						...responseDocument,
						approvals: responseDocument.approvals.map((app) => app.id),
						tasks: responseDocument.tasks.map((task) => task.id)
					};

					docsPayload.push(payloadDoc);
				}

				newState = {
					...state,
					documents: normalize(docsPayload, undefined, state.documents),
					tasks: normalize(tasksPayload, undefined, state.tasks)
				};
			}
			break;
		}
		case types.ARES_GET_DOCUMENT_BY_ID:
		case types.MARK_COMPLETE_GET_DOCUMENT_BY_ID: {
			if (isApiResponseOk(action)) {
				const includeTasks = action.payload?.expand === 'tasks';
				const docResponse = _.cloneDeep(action.response);

				if (!_.isEmpty(docResponse)) {
					newState = {...state};

					if (includeTasks) {
						const documentTasks = docResponse.tasks.map((task) => {
							return {
								...task,
								taskEngagementId: action.engagementId,
								engagementName: action.engagementName,
								taskDisplayName: task.taskName,
								documents: [docResponse.id]
							};
						});

						docResponse.tasks = documentTasks.map((task) => task.id);
						newState.tasks = normalize(documentTasks, undefined, state.tasks);
					}

					docResponse.approvals = docResponse.approvals.map((app) => app.id);

					newState.documents = normalize([docResponse], undefined, state.documents);
				}
			}
			break;
		}
		case types.ADD_APPROVAL: {
			if (isApiResponseOk(action)) {
				newState = {...state};
				const newDoc = {
					...newState.documents.byId[action.response.documentId]
				};

				newDoc.approvals = newDoc.approvals
					? newDoc.approvals.concat(action.response.approvalId)
					: [action.response.approvalId];
				newState.documents = normalizedUpdate(action.response.documentId, newDoc, newState.documents);
			}

			break;
		}
		case types.DELETE_APPROVAL: {
			if (isApiResponseOk(action)) {
				const {documentId, approvalId} = action.meta.api.data;
				let updatedDocument = state.documents.byId[documentId];

				if (updatedDocument) {
					updatedDocument = {
						...updatedDocument,
						approvals: updatedDocument.approvals.filter((appId) => appId !== approvalId)
					};

					newState = {
						...state,
						documents: normalizedUpdate(documentId, updatedDocument, state.documents)
					};
				}
			}

			break;
		}
		case types.HANDOFF_TASK: {
			if (action.error) {
				return state;
			}

			const byId = {
				...state.tasks.byId
			};

			byId[action.taskId] = {
				...state.tasks.byId[action.taskId],
				currentAssignedRoleId: action.currentAssignedRoleId,
				currentAssignedToUserId: action.currentAssignedToUserId,
				currentAssignedToEmailAddress: action.currentAssignedToEmailAddress,
				currentAssignedToUserInitials: action.currentAssignedToUserInitials,
				taskStatusId: action?.meta?.api?.data?.operationType === "markcomplete" ? TaskStatusIds.Complete : TaskStatusIds.InProgress
			};

			newState = {
				...state,
				tasks: {
					...state.tasks,
					byId
				}
			};
		}
	}

	return newState || state;
}
